import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Checkbox, FormControlLabel, TextField, Button, Autocomplete } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import cloudStorageService from "services/cloudStorageService";
import Swal from "sweetalert2"; // Import SweetAlert2

function UDSCloudStorageConfig({ selectedService, onBack }) {
  const [storageSize, setStorageSize] = useState("");
  const [cloudBackupServer, setCloudBackupServer] = useState("");
  const [features, setFeatures] = useState({ smb: false, rclone: false, webdav: false });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  const isCloudBackupSelected = selectedService === "cloudBackup";

  // Notification functions
  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  const showWarningNotification = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Achtung',
      text: message,
      confirmButtonColor: '#f8bb86',
      confirmButtonText: 'OK'
    });
  };

  const storagePrices = {
    "500gb": 10,
    "1tb": 18,
    "5tb": 30,
    "10tb": 52,
    "25tb": 120,
  };

  const featurePrices = {
    smb: 2,
    rclone: 3,
    webdav: 1,
  };

  const calculatePrice = () => {
    let price = storagePrices[storageSize] || 0;
    if (!isCloudBackupSelected) {
      Object.keys(features).forEach((feature) => {
        if (features[feature]) price += featurePrices[feature];
      });
    }
    setTotalPrice(price);
  };

  useEffect(() => {
    if (isCloudBackupSelected) {
      setFeatures({ smb: false, rclone: false, webdav: false }); // Features zurücksetzen
    }
    // Automatisch den Preis berechnen, wenn sich die Auswahl ändert
    calculatePrice();
  }, [isCloudBackupSelected, storageSize, features]);

  const handleOrder = async () => {
    if (agreedToTerms) {
      const orderDetails = {
        storageSize,
        cloudBackupServer: isCloudBackupSelected ? cloudBackupServer : null,
        features: !isCloudBackupSelected ? features : {},
        totalPrice,
      };

      try {
        const response = await cloudStorageService.orderCloudStorage(orderDetails);

        if (response.status === 200) {
          showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
          setTimeout(() => {
            window.location.href = "/iaas/server";
          }, 2000);
        } else {
          console.error("Unerwartete Antwort:", response);
          showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
        }
      } catch (error) {
        console.error("Fehler bei der Bestellung:", error);
        showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
      }
    } else {
      showWarningNotification("Bitte stimmen Sie den AGBs zu, um fortzufahren.");
    }
  };

  const isOrderDisabled = !storageSize || (isCloudBackupSelected && !cloudBackupServer) || !agreedToTerms;

  return (
    <SoftBox>
      <SoftTypography variant="h4" mb={2}>
        UDS Cloud Storage Konfiguration
      </SoftTypography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SoftTypography variant="h6">Speichergröße</SoftTypography>
          <Autocomplete
            options={[
              { label: "500 GB", value: "500gb" },
              { label: "1 TB", value: "1tb" },
              { label: "5 TB", value: "5tb" },
              { label: "10 TB", value: "10tb" },
              { label: "25 TB", value: "25tb" },
            ]}
            getOptionLabel={(option) => option.label}
            value={storageSize ? { label: `${storageSize.toUpperCase()}`, value: storageSize } : null}
            onChange={(event, newValue) => setStorageSize(newValue ? newValue.value : "")}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Speichergröße auswählen" />
            )}
          />
        </Grid>
        {isCloudBackupSelected && (
          <Grid item xs={12}>
            <SoftTypography variant="h6">Cloud Backup Server</SoftTypography>
            <Autocomplete
              options={[
                { label: "Server 1", value: "server1" },
                { label: "Server 2", value: "server2" },
                { label: "Server 3", value: "server3" },
              ]}
              getOptionLabel={(option) => option.label}
              value={cloudBackupServer ? { label: `Server ${cloudBackupServer.slice(-1)}`, value: cloudBackupServer } : null}
              onChange={(event, newValue) => setCloudBackupServer(newValue ? newValue.value : "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Cloud Backup Server auswählen" />
              )}
            />
          </Grid>
        )}
        {!isCloudBackupSelected && (
          <Grid item xs={12}>
            <SoftTypography variant="h6">Features</SoftTypography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={features.smb}
                  onChange={() => setFeatures((prev) => ({ ...prev, smb: !prev.smb }))}
                />
              }
              label="SMB"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={features.rclone}
                  onChange={() => setFeatures((prev) => ({ ...prev, rclone: !prev.rclone }))}
                />
              }
              label="RClone/SSH"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={features.webdav}
                  onChange={() => setFeatures((prev) => ({ ...prev, webdav: !prev.webdav }))}
                />
              }
              label="WebDAV"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <SoftTypography variant="h6">Gesamtpreis: {totalPrice} €</SoftTypography>
          <Button variant="contained" color="primary" onClick={calculatePrice}>
            Preis berechnen
          </Button>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            }
            label={
              <SoftTypography variant="body2">
                Ich akzeptiere die{" "}
                <a href="/agb" target="_blank" rel="noopener noreferrer">
                  AGBs
                </a>{" "}
                und bestelle das Produkt kostenpflichtig.
              </SoftTypography>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <SoftBox display="flex" justifyContent="space-between">
            <Button variant="contained" color="light" onClick={onBack}>
              Zurück
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleOrder}
              disabled={isOrderDisabled}
            >
              Bestellen
            </Button>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

UDSCloudStorageConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
  selectedService: PropTypes.string.isRequired,
};

export default UDSCloudStorageConfig;
